import { useEffect } from 'react';
import { initJQueryPlugins } from './initJQueryPlugins';

/**
 * 다음 프레임 대기 유틸리티 함수
 */
const waitForNextFrame = () => {
  return new Promise(resolve => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        resolve();
      });
    });
  });
};

/**
 * AOS가 로드될 때까지 대기하는 Promise 함수
 */
const waitForAOS = () => {
  return new Promise((resolve, reject) => {
    // 이미 로드되어 있으면 즉시 resolve
    if (typeof window.AOS !== 'undefined') {
      resolve(window.AOS);
      return;
    }

    // AOS가 로드될 때까지 대기 (최대 5초, 약 100프레임)
    const maxAttempts = 100;
    let attempts = 0;
    const startTime = Date.now();
    const timeout = 5000; // 5초 타임아웃

    const checkAOS = () => {
      attempts++;
      const elapsed = Date.now() - startTime;

      if (typeof window.AOS !== 'undefined') {
        resolve(window.AOS);
      } else if (attempts >= maxAttempts || elapsed >= timeout) {
        reject(new Error('AOS library not loaded within timeout'));
      } else {
        requestAnimationFrame(checkAOS);
      }
    };

    requestAnimationFrame(checkAOS);
  });
};

/**
 * AOS 초기화 함수
 * @param {boolean} enabled - AOS 활성화 여부
 */
const initializeAOS = async (enabled = true) => {
  try {
    const AOS = await waitForAOS();

    // AOS가 초기화되지 않았으면 초기화
    if (!window.__AOS_INITIALIZED__) {
      AOS.init({
        once: true,
        duration: 1000,
        disable: enabled ? 'mobile' : true, // enabled가 false면 완전히 비활성화
        startEvent: 'DOMContentLoaded',
        offset: 120,
        delay: 0,
        easing: 'ease',
        mirror: false,
        anchorPlacement: 'top-bottom',
      });
      window.__AOS_INITIALIZED__ = true;
    } else if (!enabled) {
      // 이미 초기화된 경우 비활성화
      if (typeof AOS.disable === 'function') {
        AOS.disable();
      } else {
        // disable 메서드가 없으면 모든 AOS 요소에서 애니메이션 제거
        const aosElements = document.querySelectorAll('[data-aos]');
        aosElements.forEach(el => {
          el.removeAttribute('data-aos');
          el.classList.remove('aos-init', 'aos-animate');
        });
      }
    }

    return AOS;
  } catch (error) {
    console.warn('AOS initialization failed:', error);
    return null;
  }
};

/**
 * jQuery 플러그인 초기화 함수
 */
const initializeJQueryPlugins = async (mainContentRef) => {
  return new Promise((resolve) => {
    // jQuery가 로드되었는지 확인
    if (typeof window.jQuery === 'undefined') {
      resolve();
      return;
    }

    // DOM 변경 감지를 위한 MutationObserver
    let observer = null;
    let changeDetected = false;

    const cleanup = () => {
      if (observer) {
        observer.disconnect();
      }
      resolve();
    };

    // main-content 영역의 DOM 변경 감지
    if (mainContentRef.current) {
      observer = new MutationObserver(() => {
        if (!changeDetected) {
          changeDetected = true;
          // 변경 감지 후 추가 프레임 대기 (플러그인 초기화 완료 보장)
          waitForNextFrame().then(cleanup);
        }
      });

      observer.observe(mainContentRef.current, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style'], // jQuery 플러그인이 주로 변경하는 속성
      });
    }

    // initJQueryPlugins 실행
    if (typeof initJQueryPlugins === 'function') {
      initJQueryPlugins();
    }

    // initMainJS 실행 (있다면)
    if (typeof window.initMainJS === 'function') {
      window.initMainJS();
    }

    // DOM 변경이 감지되지 않으면 (플러그인이 없거나 이미 초기화된 경우)
    // 짧은 대기 후 resolve
    waitForNextFrame().then(() => {
      if (!changeDetected) {
        cleanup();
      }
    });
  });
};

/**
 * AOS 새로고침 함수
 */
const refreshAOS = async () => {
  try {
    const AOS = await waitForAOS();

    // DOM이 완전히 렌더링될 때까지 대기
    await waitForNextFrame();

    // refreshHard 실행
    if (AOS && typeof AOS.refreshHard === 'function') {
      AOS.refreshHard();
    } else if (AOS && typeof AOS.refresh === 'function') {
      AOS.refresh();
    }
  } catch (error) {
    console.warn('AOS refresh failed:', error);
  }
};

/**
 * 경로 변경 시 AOS 초기화 및 새로고침 커스텀 훅
 */
export const useAOSRefresh = (locationPathname, mainContentRef) => {
  // ThemeContext에서 AOS 활성화 상태 확인
  // useTheme을 직접 import하면 순환 참조 문제가 발생할 수 있으므로 window 객체를 통해 접근
  const getAOSEnabled = () => {
    if (typeof window === 'undefined') return true;
    const themeContext = window.__ThemeContextInstance;
    if (!themeContext || !themeContext.themeConfig) return true;
    return themeContext.themeConfig.aosEnabled !== false; // 기본값: true
  };

  useEffect(() => {
    let isMounted = true;
    let observer = null;

    const handleRouteChange = async () => {
      // AOS 활성화 상태 재확인 (실시간 반영)
      const currentAOSState = getAOSEnabled();

      // AOS가 비활성화되어 있으면 스킵
      // GridRow에서 이미 AOS 속성 생성을 막으므로, 여기서는 AOS 초기화만 방지
      if (!currentAOSState) {
        // AOS가 이미 초기화되어 있다면 비활성화 (이미 렌더링된 요소 정리용)
        if (typeof window.AOS !== 'undefined' && window.AOS) {
          try {
            if (typeof window.AOS.disable === 'function') {
              window.AOS.disable();
            }
          } catch (error) {
            console.warn('AOS 비활성화 실패:', error);
          }
        }
        return;
      }

      // AOS 초기화 (활성화 상태)
      await initializeAOS(true);

      if (!isMounted) return;

      // Outlet이 렌더링될 때까지 대기 (MutationObserver 사용)
      await new Promise((resolve) => {
        if (!mainContentRef.current) {
          // ref가 아직 설정되지 않았으면 짧은 대기 후 재시도
          waitForNextFrame().then(resolve);
          return;
        }

        // MutationObserver로 DOM 변경 감지
        observer = new MutationObserver((mutations, obs) => {
          // 변경사항이 있으면 약간의 지연 후 resolve
          waitForNextFrame().then(() => {
            obs.disconnect();
            resolve();
          });
        });

        // main-content 영역의 변경사항 감지
        observer.observe(mainContentRef.current, {
          childList: true,
          subtree: true,
          attributes: false,
        });

        // 타임아웃: requestAnimationFrame으로 대기 (setTimeout 대신)
        let timeoutFrames = 0;
        const maxFrames = 120; // 약 2초 (60fps 기준)

        const checkTimeout = () => {
          timeoutFrames++;
          if (timeoutFrames >= maxFrames) {
            if (observer) {
              observer.disconnect();
            }
            resolve();
          } else {
            requestAnimationFrame(checkTimeout);
          }
        };
        requestAnimationFrame(checkTimeout);
      });

      if (!isMounted) return;

      // 추가 프레임 대기 (레이아웃 계산 완료 보장)
      await waitForNextFrame();

      if (!isMounted) return;

      // jQuery 플러그인 초기화 (AOS refresh 전에 실행)
      await initializeJQueryPlugins(mainContentRef);

      if (!isMounted) return;

      // jQuery 플러그인 초기화로 인한 DOM 변경 대기
      await waitForNextFrame();

      if (!isMounted) return;

      // AOS 새로고침 (jQuery 플러그인 초기화 후 실행)
      // AOS가 활성화되어 있을 때만 실행
      const currentAOSEnabled = getAOSEnabled();
      if (currentAOSEnabled) {
        await refreshAOS();
      }
    };

    handleRouteChange();

    return () => {
      isMounted = false;
      if (observer) {
        observer.disconnect();
      }
    };
  }, [locationPathname, mainContentRef]);
};
